/**
 * Session handling for the dashboard.
 *
 * The token is issued by the server and is the only thing that opens the API.
 * Nothing here decides whether a person is allowed in — the server does that —
 * so a tampered client gains nothing but an empty screen.
 */
import axios from 'axios';

const TOKEN_KEY = 'rdi_session_v1';

export const API_BASE = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_BASE || 'http://localhost:3001');

const listeners = new Set();

export function onAuthChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function announce(signedIn) {
  listeners.forEach((fn) => {
    try { fn(signedIn); } catch (_) { /* a bad listener must not break sign-out */ }
  });
}

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
}

function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (_) { /* private mode — the session simply lasts this tab */ }
}

export function authHeader() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Throws with a readable message the login screen can show as-is. */
export async function login(password) {
  try {
    const { data } = await axios.post(`${API_BASE}/login`, { password }, { timeout: 20000 });
    if (!data || !data.token) throw new Error('No session returned.');
    setToken(data.token);
    announce(true);
    return true;
  } catch (err) {
    const msg = err.response && err.response.data && err.response.data.error;
    throw new Error(msg || 'Could not reach the server.');
  }
}

export async function logout() {
  const token = getToken();
  setToken(null);
  announce(false);
  if (!token) return;
  try {
    await axios.post(`${API_BASE}/logout`, {}, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 8000,
    });
  } catch (_) { /* the local token is already gone, which is what matters */ }
}

/** Asks the server whether the stored token is still good. */
export async function checkSession() {
  if (!getToken()) return false;
  try {
    const { data } = await axios.get(`${API_BASE}/session`, {
      headers: authHeader(), timeout: 12000,
    });
    if (!data || !data.valid) {
      setToken(null);
      return false;
    }
    return true;
  } catch (err) {
    // A network failure is not proof the session is dead, so the token stays
    // and the caller can retry rather than throwing the user out.
    if (err.response && err.response.status === 401) {
      setToken(null);
      return false;
    }
    throw err;
  }
}

/**
 * Changes the dashboard password. The server signs every other device out and
 * returns a fresh token for this one, which replaces the stored session so the
 * user is not bounced to the login screen straight after succeeding.
 */
export async function changePassword(currentPassword, newPassword) {
  try {
    const { data } = await axios.post(
      `${API_BASE}/change-password`,
      { currentPassword, newPassword },
      { headers: authHeader(), timeout: 20000 },
    );
    if (data && data.token) setToken(data.token);
    return true;
  } catch (err) {
    const msg = err.response && err.response.data && err.response.data.error;
    throw new Error(msg || 'Could not reach the server.');
  }
}

/** Called when the API rejects a token mid-session. */
export function handleUnauthorized() {
  if (getToken()) {
    setToken(null);
    announce(false);
  }
}
